/**
 * ConfirmDialog — small reusable shadcn AlertDialog wrapper for in-app
 * confirmation prompts. Replaces blocking `window.confirm` calls with a
 * styled modal consistent with the rest of the UI.
 */

import * as React from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'destructive' | 'default';
  requireTypedName?: { value: string; label: string };
  onConfirm: () => void | Promise<void>;
  onError?: (err: unknown) => void;
  /**
   * When true, suppress Radix's "return focus to trigger" autofocus on
   * close. Set this when the ConfirmDialog is mounted outside of the React
   * subtree that owns its trigger (e.g. the app-level
   * `<GlobalConfirmDialog />`) — otherwise the focus restoration can fall
   * through to the body, which a surrounding Radix Dialog interprets as a
   * focus-outside event and auto-closes itself.
   */
  preventCloseAutoFocus?: boolean;
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  requireTypedName,
  onConfirm,
  onError,
  preventCloseAutoFocus = false,
}: ConfirmDialogProps) {
  const [typedName, setTypedName] = React.useState('');
  const inputId = React.useId();
  const typedInputTracked = React.useRef(false);

  React.useEffect(() => {
    setTypedName('');
    typedInputTracked.current = false;
  }, [open, requireTypedName?.value]);

  // S5 (review C M1): submission latch. The ref refuses a second click delivered before
  // React re-renders the disabled button; the state drives the disabled UI. While the
  // action runs the dialog cannot be dismissed (cancelling would not stop it).
  const pendingRef = React.useRef(false);
  const [pending, setPending] = React.useState(false);

  const handleOpenChange = (next: boolean) => {
    if (!next && pendingRef.current) return;
    if (!next) setTypedName('');
    onOpenChange(next);
  };

  // S5 (review B3): the dialog stays open until the action settles; a rejection keeps it
  // open (retryable) and reports through `onError`. While it runs the confirm button shows
  // a spinner and a screen-reader "Working…" status (review E F6).
  const handleConfirm = async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    let succeeded = false;
    try {
      await onConfirm();
      succeeded = true;
    } catch (err) {
      onError?.(err);
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
    if (succeeded) handleOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent
        data-testid="confirm-dialog"
        onCloseAutoFocus={preventCloseAutoFocus ? (e) => e.preventDefault() : undefined}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {requireTypedName && (
          <div className="grid gap-2">
            <Label htmlFor={inputId}>{requireTypedName.label}</Label>
            <Input
              id={inputId}
              value={typedName}
              onChange={(event) => setTypedName(event.target.value)}
              onKeyDown={(event) => {
                // Enter confirms once the name matches (S5 review F11); `handleConfirm`'s latch still refuses a repeat.
                if (event.key !== 'Enter' || event.nativeEvent.isComposing || typedName !== requireTypedName.value) return;
                event.preventDefault();
                void handleConfirm();
              }}
              onFocus={() => {
                if (typedInputTracked.current) return;
                typedInputTracked.current = true;
                void window.electronAPI?.analytics?.track('feature:confirm_typed_name_focus', { surface: 'confirm_dialog' }).catch(() => undefined);
              }}
              autoComplete="off"
              data-testid="confirm-dialog-typed-name"
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-dialog-cancel" disabled={pending}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              void handleConfirm();
            }}
            data-testid="confirm-dialog-confirm"
            aria-busy={pending || undefined}
            disabled={pending || (requireTypedName !== undefined && typedName !== requireTypedName.value)}
            className={cn(
              variant === 'destructive' && buttonVariants({ variant: 'destructive' }),
            )}
          >
            {pending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" data-testid="confirm-dialog-pending" />}
            {confirmLabel}
            {pending && <span role="status" className="sr-only">Working…</span>}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
